// The app shell (mockup layout): top bar with the wordmark, search and user; side menu; footer.

import { useState, type ReactNode } from 'react';
import type { Me } from '../api.ts';
import { zoneTone } from '../board-state.ts';
import type { AlertNotification } from '../notifications.ts';
import { NotificationsBell } from './NotificationsBell.tsx';
import {
  BoardIcon,
  DatabaseIcon,
  HelpIcon,
  InfoIcon,
  LogoutIcon,
  MoonIcon,
  PatientIcon,
  PlayIcon,
  SearchIcon,
  ShieldIcon,
  SunIcon,
} from '../icons.tsx';
import logo from '../assets/logo.png';
import { href, type Route } from '../router.ts';
import { useTheme } from '../theme.ts';

/** The role the policy lets acknowledge alerts (`alert:acknowledge`, packages/domain policy.ts). */
export const ACKNOWLEDGE_ROLE = 'ed_coordinator';

const ROLE_LABELS: Readonly<Record<string, string>> = {
  ed_clinician: 'ED clinician',
  ed_coordinator: 'ED coordinator',
  site_admin: 'Site administrator',
  support: 'Support',
};

export function Wordmark() {
  return (
    <a className="wordmark" href={href({ page: 'board' })} aria-label="Diagnuvo ED, ED board">
      <img className="wordmark-logo" src={logo} alt="" width={40} height={40} />
      <span className="wordmark-text">
        <span className="wordmark-name">
          DIAGNUVO <span className="wordmark-ed">ED</span>
        </span>
        <span className="wordmark-tag">Smart Emergency Care</span>
      </span>
    </a>
  );
}

function ThemeButton() {
  const [theme, toggle] = useTheme();
  const next = theme === 'dark' ? 'light' : 'dark';
  return (
    <button
      type="button"
      className="theme-button"
      onClick={toggle}
      aria-label={`Use the ${next} theme`}
      title={`Use the ${next} theme`}
    >
      {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
    </button>
  );
}

export function SignOutButton({ className = 'btn btn-ghost' }: { className?: string }) {
  return (
    <form method="post" action="/auth/logout">
      <button type="submit" className={className}>
        <LogoutIcon /> Sign out
      </button>
    </form>
  );
}

export function Layout({
  me,
  route,
  query,
  onQuery,
  notifications,
  siteId,
  children,
}: {
  me: Me;
  siteId: string;
  route: Route;
  query: string;
  onQuery: (q: string) => void;
  /** Recorded 4-hour alerts on the board rows, for the header bell. */
  notifications: readonly AlertNotification[];
  children: ReactNode;
}) {
  const [menu, setMenu] = useState(false);
  const role = me.roles.map((r) => ROLE_LABELS[r] ?? r).join(', ') || 'No role yet';
  return (
    <div className="shell">
      <header className="topbar">
        <Wordmark />
        <label className="search">
          <SearchIcon />
          <input
            type="search"
            placeholder="Search patients by name, MRN, NHS number, bed or complaint…"
            value={query}
            onChange={(e) => {
              onQuery(e.target.value);
              if (route.page !== 'board') window.location.hash = href({ page: 'board' });
            }}
            aria-label="Search patients"
          />
        </label>
        <span className="badge badge-demo">
          <DatabaseIcon size={16} /> Simulated demonstration data
        </span>
        <NotificationsBell
          items={notifications}
          siteId={siteId}
          canAcknowledge={me.roles.includes(ACKNOWLEDGE_ROLE)}
        />
        <ThemeButton />
        <div className="user">
          <button
            type="button"
            className="user-button"
            aria-expanded={menu}
            onClick={() => setMenu((m) => !m)}
          >
            <span className="user-avatar">
              <ShieldIcon size={20} />
            </span>
            <span className="user-text">
              <span className="user-name">
                {me.sites.length > 0 ? `Site ${me.sites.join(', ')}` : 'Signed in'}
              </span>
              <span className="user-role">{role}</span>
            </span>
          </button>
          {menu && (
            <div className="user-menu" role="menu">
              <SignOutButton className="menu-item" />
            </div>
          )}
        </div>
      </header>

      <nav className="sidebar" aria-label="Main">
        <a
          className={`nav-item${route.page === 'board' ? ' active' : ''}`}
          href={href({ page: 'board' })}
        >
          <BoardIcon /> ED board
        </a>
        <span className={`nav-item${route.page === 'patient' ? ' active' : ' disabled'}`}>
          <PatientIcon /> Patient
        </span>
        <a
          className={`nav-item${route.page === 'demo' ? ' active' : ''}`}
          href={href({ page: 'demo' })}
        >
          <PlayIcon /> Scenario demo
        </a>
        <div className="nav-sep" />
        <a
          className={`nav-item${route.page === 'help' ? ' active' : ''}`}
          href={href({ page: 'help' })}
        >
          <HelpIcon /> How to read this
        </a>
        <div className="nav-foot">
          <span className="nav-site">Synthetic Hospital (SYN)</span>
          <span>Phase 1 prototype</span>
        </div>
      </nav>

      <main className="content">{children}</main>

      <footer className="footer">
        <span>
          Simulated demonstration data. Supports clinician situational awareness only. Clinician
          judgement remains required.
        </span>
        <span className="footer-right">DIAGNUVO ED · Phase 1 prototype · v0.1</span>
      </footer>
    </div>
  );
}

/** The zone colour marker. Decorative: the zone or bed text next to it carries the meaning. */
export function ZoneDot({ code }: { code: string | undefined }) {
  return <span className={`zone-dot zone-dot-${zoneTone(code)}`} aria-hidden="true" />;
}

export function Banner({ children }: { children: ReactNode }) {
  return (
    <p className="banner" role="note">
      <InfoIcon /> <span>{children}</span>
    </p>
  );
}

export function Card({
  title,
  icon,
  info,
  aside,
  children,
  className = '',
}: {
  title: ReactNode;
  icon?: ReactNode;
  info?: string;
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`card ${className}`}>
      <header className="card-head">
        <h3>
          {icon}
          {title}
        </h3>
        {aside}
        {info !== undefined && (
          <span className="info" title={info} aria-label={info} tabIndex={0}>
            <InfoIcon size={16} />
          </span>
        )}
      </header>
      {children}
    </section>
  );
}
